import React from 'react';
import PropTypes from 'prop-types';
import OfferAssetPFClientComponent from './OfferAssetPFClientComponent';
import OfferAssetPJClientComponent from './OfferAssetPJClientComponent';
import OfferAssetClientSummary from './OfferAssetClientSummary';
import { Grid } from '@mui/material';
import Mail from '@mui/icons-material/Mail';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { withTranslation } from 'react-i18next';
import { withRights } from 'providers/RightsConsumer';
import { useApolloClient } from '@apollo/client';
import { CollapseCard } from '@totalsoft/rocket-ui';

export const OfferAssetClientCard = ({
  client,
  clientDataSummary,
  isDirty,
  expanded,
  saving,
  relations,
  gdpr,
  validationInfo,
  defaultRelationId,
  onSave,
  onPropertyChange,
  onUniqueIdBlur,
  loadGDPR,
  onGDPRPrint,
  printingGdpr,
  onCardToggle,
  t,
  partnerTypes,
  rights,
  defaultPartnerType,
  legalForms,
  loadLegalPartners,
  pfPartners,
  loadCountries,
  loadCounties,
  setIsPartnerFromModal,
  loadCities,
  citizenships,
  processing,
  isClientLoaded,
  resetGDPR,
  dirtyInfo,
  offerForCompanies,
  document
}) => {
  const apolloClient = useApolloClient();

  const invalidateCachedInsuranceData = () => {
    const { documentId, siteId } = document;
    const offerCacheId = apolloClient.cache.identify({
      __typename: 'LeasingOffer',
      documentId,
      siteId
    });

    apolloClient.cache.evict({ id: offerCacheId, fieldName: 'leasingSimulation' });
    apolloClient.cache.gc();
  };

  const handleSave = () => {
    onSave();
    invalidateCachedInsuranceData();
  };

  return (
    <CollapseCard
      id={'offerAssetClientCard'}
      icon={Mail}
      title={t('OfferEdit.Client.ClientData')}
      expanded={expanded}
      toggleOnHeaderClick
      onToggle={(_, expanded) => onCardToggle(_, !expanded)}
      subheader={<OfferAssetClientSummary clientDataSummary={clientDataSummary} isClientLoaded={isClientLoaded} />}
      hideSubheaderOnExpand
    >
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12} md={6}>
          <Autocomplete
            id={'offerAssetClientCard_partnerType'}
            disabled={rights.readOnlyFull || !rights.financedAssetClientWrite || !offerForCompanies || processing}
            fullWidth
            value={client.partnerType || defaultPartnerType}
            valueKey='partnerTypeId'
            labelKey='partnerTypeName'
            label={t('OfferEdit.Client.PartnerType')}
            options={partnerTypes}
            onChange={onPropertyChange('partnerType')}
            isClearable={false}
          />
        </Grid>
        {client.partnerType &&
          (client.partnerType.typeCheck.isPF ? (
            <OfferAssetPFClientComponent
              client={client}
              validationInfo={validationInfo}
              isDirty={isDirty}
              isClientLoaded={isClientLoaded}
              onSave={handleSave}
              saving={saving}
              onPropertyChange={onPropertyChange}
              onUniqueIdBlur={onUniqueIdBlur}
              relations={relations}
              loadGDPR={loadGDPR}
              gdpr={gdpr}
              onGDPRPrint={onGDPRPrint}
              printingGdpr={printingGdpr}
              defaultRelationId={defaultRelationId}
              setIsPartnerFromModal={setIsPartnerFromModal}
              pfPartners={pfPartners}
              loadCountries={loadCountries}
              loadCounties={loadCounties}
              loadCities={loadCities}
              citizenships={citizenships}
              processing={processing}
              resetGDPR={resetGDPR}
              dirtyInfo={dirtyInfo}
            />
          ) : (
            <OfferAssetPJClientComponent
              client={client}
              isClientLoaded={isClientLoaded}
              validationInfo={validationInfo}
              isDirty={isDirty}
              onSave={handleSave}
              saving={saving}
              onPropertyChange={onPropertyChange}
              onUniqueIdBlur={onUniqueIdBlur}
              setIsPartnerFromModal={setIsPartnerFromModal}
              partnerTypeCode={client.partnerType?.partnerTypeCode}
              partnerTypeId={client.partnerType?.partnerTypeId}
              legalForms={legalForms}
              loadLegalPartners={loadLegalPartners}
              processing={processing}
            />
          ))}
      </Grid>
    </CollapseCard>
  );
};

OfferAssetClientCard.propTypes = {
  client: PropTypes.object.isRequired,
  clientDataSummary: PropTypes.object.isRequired,
  validationInfo: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onUniqueIdBlur: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  onCardToggle: PropTypes.func.isRequired,
  relations: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired,
  gdpr: PropTypes.object.isRequired,
  loadGDPR: PropTypes.func.isRequired,
  onGDPRPrint: PropTypes.func.isRequired,
  printingGdpr: PropTypes.bool.isRequired,
  defaultRelationId: PropTypes.number,
  defaultPartnerType: PropTypes.object,
  partnerTypes: PropTypes.array.isRequired,
  loadLegalPartners: PropTypes.func.isRequired,
  legalForms: PropTypes.array.isRequired,
  loadCountries: PropTypes.func.isRequired,
  loadCities: PropTypes.func.isRequired,
  loadCounties: PropTypes.func.isRequired,
  setIsPartnerFromModal: PropTypes.func.isRequired,
  citizenships: PropTypes.array.isRequired,
  processing: PropTypes.bool,
  isClientLoaded: PropTypes.bool,
  resetGDPR: PropTypes.func.isRequired,
  dirtyInfo: PropTypes.object.isRequired,
  offerForCompanies: PropTypes.bool.isRequired,
  pfPartners: PropTypes.func.isRequired,
  document: PropTypes.object.isRequired
};

export default withTranslation()(withRights(OfferAssetClientCard));
